import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Button, TextField, MenuItem,
  Select, FormControl, InputLabel, IconButton, Chip, Tooltip,
  Divider, Collapse, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  AttachMoney as MoneyIcon, AccountBalance as BankIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import salesAPI from '../../api/sales';

const currency = (n) => n != null ? `$${Number(n).toLocaleString()}` : '—';

const FUNDING_TYPES = [
  'MCA', 'Term Loan', 'Line of Credit', 'SBA',
  'Equipment Financing', 'Invoice Factoring', 'Revenue Based', 'Other',
];
const PAYMENT_FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
];
const FUNDING_TYPE_COLORS = {
  'MCA': '#7c3aed',
  'Term Loan': '#2563eb',
  'Line of Credit': '#0891b2',
  'SBA': '#059669',
  'Equipment Financing': '#d97706',
  'Invoice Factoring': '#dc2626',
  'Revenue Based': '#db2777',
  'Other': '#64748b',
};

const EMPTY_FORM = {
  fundingType: '',
  bankName: '',
  iso: '',
  fundingAmount: '',
  paymentFrequency: '',
  numberOfPayments: '',
  totalRepayment: '',
  commissionEarned: '',
  commissionStatus: 'pending',
  fundedDate: '',
  notes: '',
};

export default function FundingsTab({ lead, onLeadUpdated }) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fundings = lead?.fundings || [];

  // Compute totals
  const totalFunded = fundings.reduce((s, f) => s + (f.fundingAmount || 0), 0);
  const totalRevenue = fundings.reduce((s, f) => s + (f.commissionEarned || 0), 0);
  const totalPending = fundings.filter(f => f.commissionStatus === 'pending').reduce((s, f) => s + (f.commissionEarned || 0), 0);
  const totalReceived = fundings.filter(f => f.commissionStatus === 'received').reduce((s, f) => s + (f.commissionEarned || 0), 0);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (funding) => {
    setForm({
      fundingType: funding.fundingType || '',
      bankName: funding.bankName || '',
      iso: funding.iso || '',
      fundingAmount: funding.fundingAmount || '',
      paymentFrequency: funding.paymentFrequency || '',
      numberOfPayments: funding.numberOfPayments || '',
      totalRepayment: funding.totalRepayment || '',
      commissionEarned: funding.commissionEarned || '',
      commissionStatus: funding.commissionStatus || 'pending',
      fundedDate: funding.fundedDate ? new Date(funding.fundedDate).toISOString().split('T')[0] : '',
      notes: funding.notes || '',
    });
    setEditingId(funding._id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.fundingType || !form.fundingAmount) {
      toast.warning('Funding type and amount are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        fundingAmount: parseFloat(form.fundingAmount) || 0,
        numberOfPayments: form.numberOfPayments ? parseInt(form.numberOfPayments) : undefined,
        totalRepayment: form.totalRepayment ? parseFloat(form.totalRepayment) : undefined,
        commissionEarned: form.commissionEarned ? parseFloat(form.commissionEarned) : 0,
      };

      let res;
      if (editingId) {
        res = await salesAPI.updateFunding(lead._id, editingId, payload);
        toast.success('Funding updated');
      } else {
        res = await salesAPI.addFunding(lead._id, payload);
        toast.success('Funding added');
      }
      onLeadUpdated(res.data.data);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save funding');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fundingId) => {
    if (!window.confirm('Delete this funding record?')) return;
    try {
      const res = await salesAPI.deleteFunding(lead._id, fundingId);
      onLeadUpdated(res.data.data);
      toast.success('Funding removed');
    } catch {
      toast.error('Failed to delete funding');
    }
  };

  return (
    <Box>
      {/* ── Revenue Summary Cards ──────────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2.5 }}>
        <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Total Funded</Typography>
          <Typography variant="h6" fontWeight={800} color="primary.main">{currency(totalFunded)}</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
          <Typography variant="h6" fontWeight={800} color="success.main">{currency(totalRevenue)}</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Commission Pending</Typography>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#d97706' }}>{currency(totalPending)}</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Commission Received</Typography>
          <Typography variant="h6" fontWeight={800} color="success.dark">{currency(totalReceived)}</Typography>
        </Paper>
      </Box>

      {/* ── Add Funding Button ─────────────────────────────────────────────── */}
      {!showForm && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => { resetForm(); setShowForm(true); }}
          sx={{ mb: 2, borderStyle: 'dashed' }}
        >
          Add Funding
        </Button>
      )}

      {/* ── Funding Form ───────────────────────────────────────────────────── */}
      <Collapse in={showForm}>
        <Paper elevation={0} sx={{ p: 2, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            {editingId ? 'Edit Funding' : 'New Funding'}
          </Typography>
          <Stack gap={1.5}>
            <Stack direction="row" gap={1.5}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Type of Funding</InputLabel>
                <Select label="Type of Funding" value={form.fundingType} onChange={handleChange('fundingType')}>
                  {FUNDING_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Funding Amount" size="small" fullWidth required type="number"
                value={form.fundingAmount} onChange={handleChange('fundingAmount')} InputProps={{ startAdornment: '$' }} />
            </Stack>
            <Stack direction="row" gap={1.5}>
              <TextField label="Bank Name" size="small" fullWidth value={form.bankName} onChange={handleChange('bankName')} />
              <TextField label="ISO" size="small" fullWidth value={form.iso} onChange={handleChange('iso')} />
            </Stack>
            <Stack direction="row" gap={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Frequency</InputLabel>
                <Select label="Payment Frequency" value={form.paymentFrequency} onChange={handleChange('paymentFrequency')}>
                  <MenuItem value="">—</MenuItem>
                  {PAYMENT_FREQUENCIES.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="# of Payments" size="small" fullWidth type="number"
                value={form.numberOfPayments} onChange={handleChange('numberOfPayments')} />
            </Stack>
            <Stack direction="row" gap={1.5}>
              <TextField label="Total Repayment" size="small" fullWidth type="number"
                value={form.totalRepayment} onChange={handleChange('totalRepayment')} InputProps={{ startAdornment: '$' }} />
              <TextField label="Commission Earned" size="small" fullWidth type="number"
                value={form.commissionEarned} onChange={handleChange('commissionEarned')} InputProps={{ startAdornment: '$' }} />
            </Stack>
            <Stack direction="row" gap={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Commission Status</InputLabel>
                <Select label="Commission Status" value={form.commissionStatus} onChange={handleChange('commissionStatus')}>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="received">Received</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Funded Date" size="small" fullWidth type="date"
                value={form.fundedDate} onChange={handleChange('fundedDate')} InputLabelProps={{ shrink: true }} />
            </Stack>
            <TextField label="Notes" size="small" fullWidth multiline minRows={2}
              value={form.notes} onChange={handleChange('notes')} />
            <Stack direction="row" gap={1} justifyContent="flex-end">
              <Button size="small" onClick={resetForm}>Cancel</Button>
              <Button size="small" variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editingId ? 'Update' : 'Add Funding'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Collapse>

      {/* ── Funding List ───────────────────────────────────────────────────── */}
      {fundings.length === 0 && !showForm && (
        <Typography color="text.secondary" textAlign="center" py={3}>No fundings recorded yet</Typography>
      )}

      <Stack gap={1.5}>
        {fundings.map((f) => {
          const isExpanded = expandedId === f._id;
          const typeColor = FUNDING_TYPE_COLORS[f.fundingType] || '#64748b';
          return (
            <Paper key={f._id} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              {/* Card header */}
              <Box
                sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => setExpandedId(isExpanded ? null : f._id)}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Chip
                      label={f.fundingType}
                      size="small"
                      sx={{ bgcolor: typeColor, color: '#fff', fontWeight: 700, fontSize: '0.7rem' }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700}>{currency(f.fundingAmount)}</Typography>
                      {f.bankName && (
                        <Typography variant="caption" color="text.secondary" noWrap>{f.bankName}</Typography>
                      )}
                    </Box>
                  </Stack>
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <Chip
                      label={f.commissionStatus === 'received' ? 'Received' : 'Pending'}
                      size="small"
                      color={f.commissionStatus === 'received' ? 'success' : 'warning'}
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </Stack>
                </Stack>
              </Box>

              {/* Expanded details */}
              <Collapse in={isExpanded}>
                <Divider />
                <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', mb: 1.5 }}>
                    <DetailItem label="Type" value={f.fundingType} />
                    <DetailItem label="Bank" value={f.bankName} />
                    <DetailItem label="ISO" value={f.iso} />
                    <DetailItem label="Funding Amount" value={currency(f.fundingAmount)} />
                    <DetailItem label="Payment Frequency" value={f.paymentFrequency ? f.paymentFrequency.replace('-', '-') : null} />
                    <DetailItem label="# Payments" value={f.numberOfPayments} />
                    <DetailItem label="Total Repayment" value={currency(f.totalRepayment)} />
                    <DetailItem label="Commission" value={currency(f.commissionEarned)} />
                    <DetailItem label="Commission Status" value={f.commissionStatus === 'received' ? '✅ Received' : '⏳ Pending'} />
                    <DetailItem label="Funded Date" value={f.fundedDate ? new Date(f.fundedDate).toLocaleDateString() : null} />
                  </Box>
                  {f.notes && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      <strong>Notes:</strong> {f.notes}
                    </Typography>
                  )}
                  <Stack direction="row" gap={1} mt={1.5} justifyContent="flex-end">
                    <Button size="small" startIcon={<EditIcon />} onClick={(e) => { e.stopPropagation(); handleEdit(f); }}>
                      Edit
                    </Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={(e) => { e.stopPropagation(); handleDelete(f._id); }}>
                      Delete
                    </Button>
                  </Stack>
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}

function DetailItem({ label, value }) {
  if (value == null || value === '' || value === '—' || value === '$0') return null;
  return (
    <Box>
      <Typography variant="caption" color="text.disabled" display="block" lineHeight={1.3}>{label}</Typography>
      <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>{value}</Typography>
    </Box>
  );
}
